import React from "react";
import Popup from "reactjs-popup";
import type { UnassignedGroupByMode } from "../unassignedPickupTypes";
import type { UnassignedRouteSortKey } from "../hooks/unassignedRouteSortFilter";
import type { SortDirection } from "../hooks/driverGroupSortFilter";
import { UnassignedWeekStrip } from "./unassignedWeekStrip";
import type { UnassignedWeekStripDay } from "./unassignedWeekStrip";
import "./driversColumnToolbar.scss";

const GROUP_BY_OPTIONS: { value: UnassignedGroupByMode; label: string }[] = [
  { value: "shipper_address", label: "Shipper's address" },
  { value: "shipper_name", label: "Shipper's name" },
];

const SORT_OPTIONS: { value: UnassignedRouteSortKey; label: string }[] = [
  { value: "route_name", label: "Route name" },
  { value: "pickup_count", label: "Pickup count" },
  { value: "stop_count", label: "Stop count" },
];

export interface UnassignedColumnToolbarProps {
  sortKey: UnassignedRouteSortKey;
  sortDirection: SortDirection;
  onSortKeyChange: (key: UnassignedRouteSortKey) => void;
  onSortDirectionChange: (dir: SortDirection) => void;
  groupBy: UnassignedGroupByMode;
  onGroupByChange: (mode: UnassignedGroupByMode) => void;
  weekStripDays: UnassignedWeekStripDay[];
  selectedPickupFilterDateKey: string;
  onPickupFilterDateKeyChange: (dateKey: string) => void;
  matchesDefaults: boolean;
  onResetToolbar: () => void;
}

export const UnassignedColumnToolbar: React.FC<UnassignedColumnToolbarProps> = ({
  sortKey,
  sortDirection,
  onSortKeyChange,
  onSortDirectionChange,
  groupBy,
  onGroupByChange,
  weekStripDays,
  selectedPickupFilterDateKey,
  onPickupFilterDateKeyChange,
  matchesDefaults,
  onResetToolbar,
}) => {
  return (
    <div className="xgs-drivers-column-toolbar">
      <div className="xgs-drivers-column-toolbar__row">
        <label className="xgs-drivers-column-toolbar__label" htmlFor="unassigned-sort-key">
          Sort
        </label>
        <select
          id="unassigned-sort-key"
          className="xgs-drivers-column-toolbar__select"
          value={sortKey}
          onChange={(e) => onSortKeyChange(e.target.value as UnassignedRouteSortKey)}
        >
          {SORT_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <label className="xgs-drivers-column-toolbar__sr-only" htmlFor="unassigned-sort-dir">
          Sort direction
        </label>
        <select
          id="unassigned-sort-dir"
          className="xgs-drivers-column-toolbar__select xgs-drivers-column-toolbar__select--narrow"
          value={sortDirection}
          onChange={(e) => onSortDirectionChange(e.target.value as SortDirection)}
          aria-label="Sort direction"
        >
          <option value="asc">Asc</option>
          <option value="desc">Desc</option>
        </select>
        <Popup
          trigger={
            <button
              type="button"
              className="xgs-drivers-column-toolbar__filter-btn"
              aria-haspopup="dialog"
            >
              Filters{!matchesDefaults ? " · active" : ""}
            </button>
          }
          position="bottom left"
          closeOnDocumentClick
          on={["click"]}
          contentStyle={{ padding: 0, border: "none", zIndex: 1200 }}
        >
          {(close: () => void) => (
            <div
              className="xgs-drivers-column-toolbar__popover xgs-drivers-column-toolbar__popover--unassigned-filters"
              role="dialog"
              aria-label="Unassigned pickup filters"
            >
              <div className="xgs-drivers-column-toolbar__popover-section">
                <div className="xgs-drivers-column-toolbar__popover-title">Pickup date</div>
                <UnassignedWeekStrip
                  days={weekStripDays}
                  selectedDateKey={selectedPickupFilterDateKey}
                  onSelectDateKey={onPickupFilterDateKeyChange}
                />
              </div>
              <div className="xgs-drivers-column-toolbar__popover-section">
                <div className="xgs-drivers-column-toolbar__popover-title">Group by</div>
                <div className="xgs-drivers-column-toolbar__chips">
                  {GROUP_BY_OPTIONS.map((o) => (
                    <button
                      key={o.value}
                      type="button"
                      className={
                        groupBy === o.value
                          ? "xgs-drivers-column-toolbar__chip xgs-drivers-column-toolbar__chip--on"
                          : "xgs-drivers-column-toolbar__chip"
                      }
                      onClick={() => onGroupByChange(o.value)}
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="xgs-drivers-column-toolbar__popover-actions">
                <button
                  type="button"
                  className="xgs-drivers-column-toolbar__link-btn"
                  onClick={() => {
                    onResetToolbar();
                    close();
                  }}
                >
                  Reset to defaults
                </button>
                <button
                  type="button"
                  className="xgs-drivers-column-toolbar__primary-btn"
                  onClick={() => close()}
                >
                  Done
                </button>
              </div>
            </div>
          )}
        </Popup>
        {!matchesDefaults && (
          <button
            type="button"
            className="xgs-drivers-column-toolbar__link-btn xgs-drivers-column-toolbar__clear-inline"
            onClick={onResetToolbar}
          >
            Reset
          </button>
        )}
      </div>
    </div>
  );
};
